import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, LogOut, ExternalLink, Download, FileText, Mail, Briefcase, Users, Inbox, UserPlus, Upload, ChevronDown, Building2, Database, Search, Star, ShieldCheck, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  adminJobs, createJob, updateJob, deleteJob,
  adminApplications, downloadResume, listAdmins, createAdmin, apiError,
  updateApplicationStatus, exportApplications,
  adminCandidates, createCandidate, adminHrForms, createHrForm, deleteHrForm, adminDownloadHrForm,
  adminClients, createClient, adminRequisitions, createRequisition, updateRequisition, deleteRequisition,
  addSubmittal, updateSubmittal, deleteSubmittal, adminClientDocs, uploadClientDoc, deleteClientDoc, adminDownloadClientDoc,
  adminTalent, createTalent, updateTalent, deleteTalent, promoteApplicant, downloadTalentResume, runDeadlineCheck,
} from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const FIELD = "w-full border border-border bg-secondary px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-brand";
const EMPTY_JOB = {
  title: "", department: "", location: "", employmentType: "Full-time",
  summary: "", description: "", applyMode: "internal", externalUrl: "",
  requisition_id: "", pay_min: "", pay_max: "", pay_unit: "year", fringe_benefits: "",
  work_arrangement: "On-site", client_type: "Federal Government", contract_program: "",
  clearance_requirement: "None", active_clearance_required: false,
  education_requirement: "None", experience_years: "", specialized_experience: "",
  application_deadline: "", anticipated_start_date: "",
  screening_questions: [], acknowledgment_required: false, acknowledgment_text: "",
  featured: false, internal_notes: "", job_status: "Open", hiring_status: "Confirmed",
};
const PIPELINE = ["applied", "screening", "phone_screen", "qualified", "finalist", "proposed", "contingent_offer", "hired", "rejected", "withdrawn"];
const PIPELINE_LABEL = { applied: "Applied", screening: "Screening", phone_screen: "Phone Screen", qualified: "Qualified", finalist: "Finalist", proposed: "Proposed to Client/Gov", contingent_offer: "Contingent Offer", hired: "Hired", rejected: "Rejected", withdrawn: "Withdrawn" };
const CLIENT_TYPES = ["Federal Government", "State Government", "Local Government", "Healthcare", "Commercial"];
const WORK_ARR = ["On-site", "Hybrid", "Remote"];
const HIRING = ["Confirmed", "Anticipated", "Contingent on Award"];
const JOB_STATUS = ["Draft", "Open", "Paused", "Closed", "Filled"];
const CLEARANCES = ["None", "Ability to Obtain", "Public Trust", "Confidential", "Secret", "Top Secret", "TS-SCI", "Other"];
const EDUCATION = ["None", "HS-GED", "Associate", "Bachelor's", "Master's", "Other"];
const QTYPES = [["yes_no", "Yes / No"], ["short", "Short answer"], ["long", "Long answer"]];
const REQ_STATUSES = ["open", "on_hold", "filled", "closed"];
const SUB_STATUSES = ["submitted", "interviewing", "offer", "hired", "declined"];

const TABS = [
  { key: "positions", label: "Positions", icon: Briefcase },
  { key: "applicants", label: "Applicants", icon: Inbox },
  { key: "talent", label: "Talent Pool", icon: Database },
  { key: "candidates", label: "Candidates", icon: UserPlus },
  { key: "clients", label: "Clients", icon: Building2 },
  { key: "team", label: "Team", icon: Users },
];

export default function AdminDashboard() {
  const { user, signOut } = useAuth();
  const [tab, setTab] = useState("positions");

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <img src="/vbg-bridge-mark-t.png" alt="VectorBridge Group" className="h-8 w-auto" />
            <div>
              <div className="font-display text-base font-bold uppercase leading-none tracking-tight text-navy">VectorBridge Console</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{user?.name || user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="border border-border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-foreground" data-testid="admin-view-site">View site</Link>
            <button onClick={signOut} data-testid="admin-logout" className="inline-flex items-center gap-2 bg-brand px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy"><LogOut size={14} /> Logout</button>
          </div>
        </div>
        <div className="mx-auto flex max-w-[1400px] gap-1 px-5 sm:px-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`} className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${tab === t.key ? "border-brand text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 py-10 sm:px-8">
        {tab === "positions" && <Positions />}
        {tab === "applicants" && <Applicants />}
        {tab === "talent" && <TalentPool />}
        {tab === "candidates" && <Candidates />}
        {tab === "clients" && <Clients />}
        {tab === "team" && <Team />}
      </main>
    </div>
  );
}

/* ---------------- Positions ---------------- */
function Positions() {
  const [jobs, setJobs] = useState(null);
  const [editing, setEditing] = useState(null); // job object or {} for new
  const load = () => adminJobs().then(setJobs).catch(() => setJobs([]));
  useEffect(() => { load(); }, []);

  const remove = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? This cannot be undone.`)) return;
    try { await deleteJob(job.id); toast.success("Position deleted."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleStatus = async (job) => {
    const next = job.job_status === "Open" ? "Closed" : "Open";
    try { await updateJob(job.id, { ...job, job_status: next }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const checkDeadlines = async () => {
    try { const r = await runDeadlineCheck(); toast.success(r.count ? `Reminder sent for ${r.count} posting(s).` : "No deadlines within the reminder window."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-light tracking-tighter">Positions</h1>
        <div className="flex items-center gap-2">
          <button onClick={checkDeadlines} data-testid="positions-check-deadlines" className="inline-flex items-center gap-2 border border-border px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors hover:border-brand hover:text-brand"><Clock size={14} /> Check deadlines</button>
          <button onClick={() => setEditing({ ...EMPTY_JOB })} data-testid="admin-new-position" className="inline-flex items-center gap-2 bg-brand px-5 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy"><Plus size={15} /> New position</button>
        </div>
      </div>

      {jobs === null ? (
        <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
      ) : jobs.length === 0 ? (
        <p className="mt-10 text-muted-foreground">No positions yet. Create your first opening.</p>
      ) : (
        <div className="mt-8 divide-y divide-border border-t border-border">
          {jobs.map((job) => (
            <div key={job.id} data-testid={`admin-job-${job.id}`} className="flex flex-wrap items-center justify-between gap-4 py-5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`h-2 w-2 rotate-45 ${job.job_status === "Open" ? "bg-brand" : "bg-muted-foreground"}`} />
                  {job.featured && <span className="border border-brand px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-brand">Featured</span>}
                  <h3 className="font-display text-xl tracking-tight">{job.title}</h3>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{job.job_status || job.status}</span>
                </div>
                <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
                  {job.requisition_id && <span className="text-brand">{job.requisition_id}</span>}
                  {job.requisition_id && " · "}{job.location} · {job.employmentType} · {job.hiring_status || "—"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleStatus(job)} data-testid={`admin-job-toggle-${job.id}`} className="border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground hover:text-foreground">{job.job_status === "Open" ? "Close" : "Reopen"}</button>
                <button onClick={() => setEditing(job)} data-testid={`admin-job-edit-${job.id}`} className="grid h-9 w-9 place-items-center border border-border text-muted-foreground hover:text-foreground"><Pencil size={14} /></button>
                <button onClick={() => remove(job)} data-testid={`admin-job-delete-${job.id}`} className="grid h-9 w-9 place-items-center border border-border text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <JobDialog job={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

function JobDialog({ job, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_JOB);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (job) setForm({ ...EMPTY_JOB, ...job, screening_questions: job.screening_questions || [] }); }, [job]);
  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const toggle = (name) => setForm((f) => ({ ...f, [name]: !f[name] }));

  const addQuestion = () => setForm((f) => ({ ...f, screening_questions: [...f.screening_questions, { id: "", question: "", type: "yes_no", required: true, disqualifying: false }] }));
  const updateQuestion = (i, key, val) => setForm((f) => ({ ...f, screening_questions: f.screening_questions.map((q, idx) => idx === i ? { ...q, [key]: val } : q) }));
  const removeQuestion = (i) => setForm((f) => ({ ...f, screening_questions: f.screening_questions.filter((_, idx) => idx !== i) }));

  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.location || !form.summary || !form.description) return toast.error("Title, location, summary and description are required.");
    if ((form.applyMode === "external" || form.applyMode === "both") && !form.externalUrl) return toast.error("Add an external apply URL.");
    if (form.screening_questions.some((q) => !q.question.trim())) return toast.error("Every screening question needs text (or remove it).");
    setSaving(true);
    try {
      const payload = {
        title: form.title, department: form.department, location: form.location,
        employmentType: form.employmentType, summary: form.summary, description: form.description,
        applyMode: form.applyMode, externalUrl: form.externalUrl,
        requisition_id: form.requisition_id,
        pay_min: num(form.pay_min), pay_max: num(form.pay_max), pay_unit: form.pay_unit, fringe_benefits: form.fringe_benefits,
        work_arrangement: form.work_arrangement, client_type: form.client_type, contract_program: form.contract_program,
        clearance_requirement: form.clearance_requirement, active_clearance_required: form.active_clearance_required,
        education_requirement: form.education_requirement, experience_years: num(form.experience_years), specialized_experience: form.specialized_experience,
        application_deadline: form.application_deadline, anticipated_start_date: form.anticipated_start_date,
        screening_questions: form.screening_questions, acknowledgment_required: form.acknowledgment_required, acknowledgment_text: form.acknowledgment_text,
        featured: form.featured, internal_notes: form.internal_notes, job_status: form.job_status, hiring_status: form.hiring_status,
      };
      if (form.id) await updateJob(form.id, payload); else await createJob(payload);
      toast.success(form.id ? "Position updated." : "Position published.");
      onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const SEL = FIELD;
  const Legend = ({ children }) => <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand sm:col-span-2">{children}</div>;
  const Label = ({ children }) => <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{children}</span>;

  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-card sm:max-w-3xl" data-testid="admin-job-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-light tracking-tighter text-navy">{form.id ? "Edit position" : "New position"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">

          <Legend>Basics</Legend>
          <input name="title" value={form.title} onChange={change} placeholder="Title *" className={`${FIELD} sm:col-span-2`} data-testid="job-title" />
          <div><Label>Requisition ID (auto-generated if blank)</Label>
            <input name="requisition_id" value={form.requisition_id} onChange={change} placeholder="VBG-GOV-2026-001" className={FIELD} data-testid="job-reqid" /></div>
          <input name="department" value={form.department} onChange={change} placeholder="Department" className={FIELD} data-testid="job-department" />
          <input name="location" value={form.location} onChange={change} placeholder="Location *" className={FIELD} data-testid="job-location" />
          <select name="employmentType" value={form.employmentType} onChange={change} className={SEL} data-testid="job-type">
            {["Full-time", "Part-time", "Contract", "Temp-to-Hire", "Temporary"].map((o) => <option key={o}>{o}</option>)}
          </select>
          <div><Label>Work Arrangement</Label>
            <select name="work_arrangement" value={form.work_arrangement} onChange={change} className={SEL} data-testid="job-workarr">{WORK_ARR.map((o) => <option key={o}>{o}</option>)}</select></div>
          <div><Label>Client Type</Label>
            <select name="client_type" value={form.client_type} onChange={change} className={SEL} data-testid="job-clienttype">{CLIENT_TYPES.map((o) => <option key={o}>{o}</option>)}</select></div>
          <input name="summary" value={form.summary} onChange={change} placeholder="One-line summary *" className={`${FIELD} sm:col-span-2`} data-testid="job-summary" />
          <textarea name="description" value={form.description} onChange={change} rows={6} placeholder="Full description *" className={`${FIELD} sm:col-span-2`} data-testid="job-description" />

          <Legend>Status</Legend>
          <div><Label>Job Status</Label>
            <select name="job_status" value={form.job_status} onChange={change} className={SEL} data-testid="job-status">{JOB_STATUS.map((o) => <option key={o}>{o}</option>)}</select></div>
          <div><Label>Hiring Status</Label>
            <select name="hiring_status" value={form.hiring_status} onChange={change} className={SEL} data-testid="job-hiringstatus">{HIRING.map((o) => <option key={o}>{o}</option>)}</select></div>

          <Legend>Compensation</Legend>
          <div><Label>Pay minimum</Label><input name="pay_min" type="number" value={form.pay_min ?? ""} onChange={change} placeholder="e.g. 90000" className={FIELD} data-testid="job-paymin" /></div>
          <div><Label>Pay maximum</Label><input name="pay_max" type="number" value={form.pay_max ?? ""} onChange={change} placeholder="e.g. 120000" className={FIELD} data-testid="job-paymax" /></div>
          <div><Label>Pay unit</Label>
            <select name="pay_unit" value={form.pay_unit} onChange={change} className={SEL} data-testid="job-payunit"><option value="year">Per year</option><option value="hour">Per hour</option></select></div>
          <input name="fringe_benefits" value={form.fringe_benefits} onChange={change} placeholder="Fringe / benefits (SCA H&W, etc.)" className={FIELD} data-testid="job-fringe" />

          <Legend>Requirements</Legend>
          <div><Label>Clearance requirement</Label>
            <select name="clearance_requirement" value={form.clearance_requirement} onChange={change} className={SEL} data-testid="job-clearance">{CLEARANCES.map((o) => <option key={o}>{o}</option>)}</select></div>
          <div><Label>Education</Label>
            <select name="education_requirement" value={form.education_requirement} onChange={change} className={SEL} data-testid="job-education">{EDUCATION.map((o) => <option key={o}>{o}</option>)}</select></div>
          <label className="flex items-center gap-2 text-sm sm:col-span-2" data-testid="job-activeclearance-label">
            <input type="checkbox" checked={form.active_clearance_required} onChange={() => toggle("active_clearance_required")} data-testid="job-activeclearance" /> Active clearance required at time of application
          </label>
          <div><Label>Experience (years)</Label><input name="experience_years" type="number" value={form.experience_years ?? ""} onChange={change} placeholder="e.g. 5" className={FIELD} data-testid="job-expyears" /></div>
          <textarea name="specialized_experience" value={form.specialized_experience} onChange={change} rows={2} placeholder="Required specialized experience (e.g. military-environment experience)" className={`${FIELD} sm:col-span-2`} data-testid="job-specexp" />

          <Legend>Dates</Legend>
          <div><Label>Application deadline (recruiting)</Label><input name="application_deadline" type="datetime-local" value={form.application_deadline || ""} onChange={change} className={FIELD} data-testid="job-deadline" /></div>
          <div><Label>Anticipated start date</Label><input name="anticipated_start_date" value={form.anticipated_start_date} onChange={change} placeholder="e.g. Upon contract award" className={FIELD} data-testid="job-startdate" /></div>

          <Legend>Apply</Legend>
          <select name="applyMode" value={form.applyMode} onChange={change} className={SEL} data-testid="job-applymode">
            <option value="internal">Apply on site</option>
            <option value="external">External link</option>
            <option value="both">Both</option>
          </select>
          {(form.applyMode === "external" || form.applyMode === "both") && (
            <input name="externalUrl" value={form.externalUrl} onChange={change} placeholder="External apply URL *" className={FIELD} data-testid="job-externalurl" />
          )}
          <label className="flex items-center gap-2 text-sm" data-testid="job-ackreq-label">
            <input type="checkbox" checked={form.acknowledgment_required} onChange={() => toggle("acknowledgment_required")} data-testid="job-ackreq" /> Require contingency acknowledgment
          </label>
          <label className="flex items-center gap-2 text-sm" data-testid="job-featured-label">
            <input type="checkbox" checked={form.featured} onChange={() => toggle("featured")} data-testid="job-featured" /> Featured / urgent (top of Careers)
          </label>
          {form.acknowledgment_required && (
            <textarea name="acknowledgment_text" value={form.acknowledgment_text} onChange={change} rows={2} placeholder="Acknowledgment text (leave blank for default contract-award wording)" className={`${FIELD} sm:col-span-2`} data-testid="job-acktext" />
          )}

          <div className="flex items-center justify-between sm:col-span-2">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand">Screening Questions</div>
            <button type="button" onClick={addQuestion} data-testid="job-add-question" className="inline-flex items-center gap-1 border border-border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] hover:border-brand hover:text-brand"><Plus size={13} /> Add</button>
          </div>
          {form.screening_questions.map((q, i) => (
            <div key={i} data-testid={`job-question-${i}`} className="border border-border p-3 sm:col-span-2">
              <div className="flex items-start gap-2">
                <input value={q.question} onChange={(e) => updateQuestion(i, "question", e.target.value)} placeholder={`Question ${i + 1}`} className={FIELD} data-testid={`job-question-text-${i}`} />
                <button type="button" onClick={() => removeQuestion(i)} data-testid={`job-question-remove-${i}`} className="grid h-10 w-10 shrink-0 place-items-center border border-border text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-4">
                <select value={q.type} onChange={(e) => updateQuestion(i, "type", e.target.value)} className="border border-border bg-secondary px-3 py-2 font-mono text-[10px] uppercase tracking-[0.1em] outline-none focus:border-brand" data-testid={`job-question-type-${i}`}>
                  {QTYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={q.required} onChange={(e) => updateQuestion(i, "required", e.target.checked)} data-testid={`job-question-required-${i}`} /> Required</label>
                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={q.disqualifying} onChange={(e) => updateQuestion(i, "disqualifying", e.target.checked)} data-testid={`job-question-disq-${i}`} /> Disqualifying</label>
              </div>
            </div>
          ))}

          <Legend>Internal (never shown publicly)</Legend>
          <input name="contract_program" value={form.contract_program} onChange={change} placeholder="Contract / Program (internal)" className={`${FIELD} sm:col-span-2`} data-testid="job-contract" />
          <textarea name="internal_notes" value={form.internal_notes} onChange={change} rows={3} placeholder="Internal notes: proposal deadlines, candidate strategy, recruiting notes…" className={`${FIELD} sm:col-span-2`} data-testid="job-internalnotes" />

          <button type="submit" disabled={saving} data-testid="job-save" className="mt-2 inline-flex items-center justify-center gap-2 bg-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy disabled:opacity-60 sm:col-span-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : null} {form.id ? "Save changes" : "Publish position"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Applicants ---------------- */
const STATUS_COLOR = { applied: "text-yellow-500", screening: "text-blue-400", phone_screen: "text-blue-400", qualified: "text-brand", finalist: "text-brand", proposed: "text-purple-500", contingent_offer: "text-purple-500", hired: "text-green-500", rejected: "text-destructive", withdrawn: "text-muted-foreground" };
const normStatus = (s) => (PIPELINE.includes(s) ? s : (s === "hired" ? "hired" : "applied"));

function Applicants() {
  const [apps, setApps] = useState(null);
  const [filter, setFilter] = useState("all");
  const [invite, setInvite] = useState(null);
  const [promote, setPromote] = useState(null);
  const load = () => adminApplications().then(setApps).catch(() => setApps([]));
  useEffect(() => { load(); }, []);

  const dl = async (a) => {
    try { await downloadResume(a.id, a.resume?.original_filename); }
    catch { toast.error("Could not download resume."); }
  };
  const setStatus = async (a, status) => {
    setApps((prev) => prev.map((x) => (x.id === a.id ? { ...x, status } : x)));
    try { await updateApplicationStatus(a.id, status); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };
  const exportCsv = async () => {
    try { await exportApplications(); } catch { toast.error("Export failed."); }
  };

  const shown = (apps || []).filter((a) => filter === "all" || normStatus(a.status) === filter);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-3xl font-light tracking-tighter">Applicants</h1>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="applicants-filter" className="border border-border bg-secondary px-3 py-2 font-mono text-[11px] uppercase tracking-[0.12em] outline-none focus:border-brand">
            <option value="all">All statuses</option>
            {PIPELINE.map((s) => <option key={s} value={s}>{PIPELINE_LABEL[s]}</option>)}
          </select>
          <button onClick={exportCsv} data-testid="applicants-export" className="inline-flex items-center gap-2 border border-border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors hover:border-brand hover:text-brand"><Download size={14} /> Export CSV</button>
        </div>
      </div>

      {apps === null ? (
        <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
      ) : shown.length === 0 ? (
        <p className="mt-10 text-muted-foreground">No applications{filter !== "all" ? ` with status "${filter}"` : " yet"}.</p>
      ) : (
        <div className="mt-8 divide-y divide-border border-t border-border">
          {shown.map((a) => (
            <div key={a.id} data-testid={`admin-app-${a.id}`} className="flex flex-wrap items-start justify-between gap-4 py-5">
              <div className="min-w-0">
                <h3 className="font-display text-xl tracking-tight">{a.name}</h3>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1 hover:text-brand"><Mail size={13} /> {a.email}</a>
                  {a.phone && <span>{a.phone}</span>}
                  {a.location && <span>{a.location}</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-[0.12em]">
                  <span className="border border-border px-2 py-1 text-brand">{a.jobTitle || a.role || "General"}</span>
                  {a.requisition_id && <span className="border border-brand px-2 py-1 text-brand">{a.requisition_id}</span>}
                  <span className="border border-border px-2 py-1 text-muted-foreground">{a.source}</span>
                  {a.disqualified && <span className="border border-destructive px-2 py-1 text-destructive">Auto-flagged</span>}
                  {a.acknowledgment_accepted && <span className="border border-border px-2 py-1 text-green-600">Ack ✓</span>}
                  <span className="px-2 py-1 text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</span>
                </div>
                {a.message && <p className="mt-3 max-w-2xl text-sm text-muted-foreground">{a.message}</p>}
                {Array.isArray(a.screening_answers) && a.screening_answers.length > 0 && (
                  <div className="mt-3 space-y-1.5 border-l-2 border-border pl-3" data-testid={`admin-app-screening-${a.id}`}>
                    {a.screening_answers.map((q, qi) => (
                      <div key={qi} className="text-xs">
                        <span className="text-muted-foreground">{q.question}{q.disqualifying ? " ⚠" : ""}</span>
                        <span className="ml-2 font-medium text-navy">{String(q.answer || "—")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <select value={normStatus(a.status)} onChange={(e) => setStatus(a, e.target.value)} data-testid={`admin-app-status-${a.id}`} className={`border border-border bg-secondary px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] outline-none focus:border-brand ${STATUS_COLOR[normStatus(a.status)]}`}>
                  {PIPELINE.map((s) => <option key={s} value={s}>{PIPELINE_LABEL[s]}</option>)}
                </select>
                <div className="flex gap-2">
                  {a.resume ? (
                    <button onClick={() => dl(a)} data-testid={`admin-app-resume-${a.id}`} className="inline-flex items-center gap-2 border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors hover:border-brand hover:text-brand"><Download size={13} /> Resume</button>
                  ) : (
                    <span className="inline-flex items-center gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground/50"><FileText size={13} /> No file</span>
                  )}
                  <button onClick={() => setPromote(a)} data-testid={`admin-app-promote-${a.id}`} className="inline-flex items-center gap-2 border border-brand px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-brand transition-colors hover:bg-brand hover:text-white"><Database size={13} /> Talent pool</button>
                  <button onClick={() => setInvite(a)} data-testid={`admin-app-invite-${a.id}`} className="inline-flex items-center gap-2 bg-brand px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy"><UserPlus size={13} /> Portal login</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <InviteDialog applicant={invite} onClose={() => setInvite(null)} onDone={() => setInvite(null)} />
      <PromoteDialog applicant={promote} onClose={() => setPromote(null)} onDone={() => setPromote(null)} />
    </div>
  );
}

function PromoteDialog({ applicant, onClose, onDone }) {
  const [form, setForm] = useState({ profession: "", skills: "", clearance: "None", military_experience: false, military_notes: "", availability: "", notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (applicant) setForm({ profession: "", skills: "", clearance: "None", military_experience: false, military_notes: "", availability: "", notes: "" }); }, [applicant]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await promoteApplicant(applicant.id, {
        profession: form.profession,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        clearance: form.clearance, military_experience: form.military_experience,
        military_notes: form.military_notes, availability: form.availability, notes: form.notes,
      });
      toast.success(`${applicant.name} added to the talent pool.`);
      onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!applicant} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-card sm:max-w-lg" data-testid="promote-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-light tracking-tighter text-navy">Add to Talent Pool</DialogTitle></DialogHeader>
        {applicant && <p className="text-sm text-muted-foreground">Enrich <span className="text-navy font-medium">{applicant.name}</span> ({applicant.email}) with searchable attributes. Resume and contact details carry over automatically.</p>}
        <form onSubmit={save} className="mt-2 grid gap-4 sm:grid-cols-2">
          <input value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} placeholder="Profession (e.g. Logistics Analyst)" className={`${FIELD} sm:col-span-2`} data-testid="promote-profession" />
          <input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="Skills (comma-separated)" className={`${FIELD} sm:col-span-2`} data-testid="promote-skills" />
          <select value={form.clearance} onChange={(e) => setForm({ ...form, clearance: e.target.value })} className={FIELD} data-testid="promote-clearance">{CLEARANCES.map((c) => <option key={c}>{c}</option>)}</select>
          <input value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} placeholder="Availability (e.g. 2 weeks)" className={FIELD} data-testid="promote-availability" />
          <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={form.military_experience} onChange={(e) => setForm({ ...form, military_experience: e.target.checked })} data-testid="promote-military" /> Military experience</label>
          {form.military_experience && <input value={form.military_notes} onChange={(e) => setForm({ ...form, military_notes: e.target.value })} placeholder="Military background (branch, years, MOS)" className={`${FIELD} sm:col-span-2`} data-testid="promote-military-notes" />}
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Recruiter notes" className={`${FIELD} sm:col-span-2`} data-testid="promote-notes" />
          <button type="submit" disabled={saving} data-testid="promote-save" className="inline-flex items-center justify-center gap-2 bg-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy disabled:opacity-60 sm:col-span-2">{saving ? <Loader2 size={16} className="animate-spin" /> : <Database size={16} />} Add to Talent Pool</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Talent Pool ---------------- */
function TalentPool() {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ q: "", clearance: "", military: false, location: "", skill: "" });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const params = {};
    if (f.q) params.q = f.q;
    if (f.clearance) params.clearance = f.clearance;
    if (f.military) params.military = "true";
    if (f.location) params.location = f.location;
    if (f.skill) params.skill = f.skill;
    try { setRows(await adminTalent(params)); } catch { setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  const remove = async (t) => {
    if (!window.confirm(`Remove ${t.name} from the talent pool?`)) return;
    try { await deleteTalent(t.id); toast.success("Removed."); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const dl = async (t) => { try { await downloadTalentResume(t.id, t.resume?.original_filename); } catch { toast.error("No resume on file."); } };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-3xl font-light tracking-tighter">Talent Pool <span className="ml-2 font-mono text-sm text-muted-foreground">{rows ? `${rows.length}` : ""}</span></h1>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search name, skill…" className={`${FIELD} pl-9`} data-testid="talent-search" />
        </div>
        <input value={f.skill} onChange={(e) => setF({ ...f, skill: e.target.value })} placeholder="Skill" className={FIELD} data-testid="talent-filter-skill" />
        <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Location" className={FIELD} data-testid="talent-filter-location" />
        <select value={f.clearance} onChange={(e) => setF({ ...f, clearance: e.target.value })} className={FIELD} data-testid="talent-filter-clearance"><option value="">Any clearance</option>{CLEARANCES.map((c) => <option key={c}>{c}</option>)}</select>
        <label className="flex items-center gap-2 border border-border bg-secondary px-4 text-sm" data-testid="talent-filter-military-label"><input type="checkbox" checked={f.military} onChange={(e) => setF({ ...f, military: e.target.checked })} data-testid="talent-filter-military" /> Military only</label>
      </div>

      {rows === null ? (
        <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
      ) : rows.length === 0 ? (
        <p className="mt-10 text-muted-foreground">No candidates match. Promote applicants from the Applicants tab to build your pipeline.</p>
      ) : (
        <div className="mt-8 divide-y divide-border border-t border-border">
          {rows.map((t) => (
            <div key={t.id} data-testid={`talent-${t.id}`} className="flex flex-wrap items-start justify-between gap-4 py-5">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <h3 className="font-display text-xl tracking-tight text-navy">{t.name}</h3>
                  {t.profession && <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-brand">{t.profession}</span>}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <a href={`mailto:${t.email}`} className="inline-flex items-center gap-1 hover:text-brand"><Mail size={13} /> {t.email}</a>
                  {t.location && <span>{t.location}</span>}
                  {t.availability && <span>Avail: {t.availability}</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-[0.12em]">
                  {t.clearance && t.clearance !== "None" && <span className="inline-flex items-center gap-1 border border-border px-2 py-1 text-muted-foreground"><ShieldCheck size={11} className="text-brand" /> {t.clearance}</span>}
                  {t.military_experience && <span className="border border-brand px-2 py-1 text-brand">Military</span>}
                  {t.requisition_id && <span className="border border-border px-2 py-1 text-muted-foreground">{t.requisition_id}</span>}
                </div>
                {t.skills?.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{t.skills.map((s, i) => <span key={i} className="border border-border bg-secondary px-2 py-0.5 text-xs text-navy">{s}</span>)}</div>}
                {t.notes && <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t.notes}</p>}
              </div>
              <div className="flex gap-2">
                {t.resume && <button onClick={() => dl(t)} data-testid={`talent-resume-${t.id}`} className="inline-flex items-center gap-2 border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors hover:border-brand hover:text-brand"><Download size={13} /> Resume</button>}
                <button onClick={() => setEditing(t)} data-testid={`talent-edit-${t.id}`} className="grid h-9 w-9 place-items-center border border-border text-muted-foreground hover:text-foreground"><Pencil size={14} /></button>
                <button onClick={() => remove(t)} data-testid={`talent-delete-${t.id}`} className="grid h-9 w-9 place-items-center border border-border text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <TalentEditDialog talent={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

function TalentEditDialog({ talent, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (talent) setForm({ ...talent, skills: (talent.skills || []).join(", ") }); }, [talent]);
  if (!form) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateTalent(talent.id, {
        name: form.name, email: form.email, phone: form.phone || "", location: form.location || "",
        profession: form.profession || "", skills: String(form.skills).split(",").map((s) => s.trim()).filter(Boolean),
        clearance: form.clearance || "None", military_experience: !!form.military_experience, military_notes: form.military_notes || "",
        availability: form.availability || "", notes: form.notes || "", status: form.status || "active",
        source_application_id: form.source_application_id || "", requisition_id: form.requisition_id || "",
      });
      toast.success("Candidate updated.");
      onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!talent} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-card sm:max-w-lg" data-testid="talent-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-light tracking-tighter text-navy">Edit candidate</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className={FIELD} data-testid="talent-edit-name" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={FIELD} data-testid="talent-edit-email" />
          <input value={form.profession || ""} onChange={(e) => setForm({ ...form, profession: e.target.value })} placeholder="Profession" className={FIELD} data-testid="talent-edit-profession" />
          <input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location" className={FIELD} data-testid="talent-edit-location" />
          <input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="Skills (comma-separated)" className={`${FIELD} sm:col-span-2`} data-testid="talent-edit-skills" />
          <select value={form.clearance || "None"} onChange={(e) => setForm({ ...form, clearance: e.target.value })} className={FIELD} data-testid="talent-edit-clearance">{CLEARANCES.map((c) => <option key={c}>{c}</option>)}</select>
          <input value={form.availability || ""} onChange={(e) => setForm({ ...form, availability: e.target.value })} placeholder="Availability" className={FIELD} data-testid="talent-edit-availability" />
          <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={!!form.military_experience} onChange={(e) => setForm({ ...form, military_experience: e.target.checked })} data-testid="talent-edit-military" /> Military experience</label>
          <select value={form.status || "active"} onChange={(e) => setForm({ ...form, status: e.target.value })} className={FIELD} data-testid="talent-edit-status">{["active", "placed", "archived"].map((s) => <option key={s}>{s}</option>)}</select>
          <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Recruiter notes" className={`${FIELD} sm:col-span-2`} data-testid="talent-edit-notes" />
          <button type="submit" disabled={saving} data-testid="talent-edit-save" className="inline-flex items-center justify-center gap-2 bg-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy disabled:opacity-60 sm:col-span-2">{saving ? <Loader2 size={16} className="animate-spin" /> : null} Save changes</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function InviteDialog({ applicant, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPassword(""); }, [applicant]);
  const create = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    setSaving(true);
    try {
      await createCandidate({ name: applicant.name, email: applicant.email, password, applicationId: applicant.id });
      toast.success(`Portal account created for ${applicant.email}. Share the password with the candidate.`);
      onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!applicant} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-border bg-card sm:max-w-md" data-testid="invite-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-light tracking-tighter">Create candidate portal login</DialogTitle></DialogHeader>
        {applicant && (
          <form onSubmit={create} className="space-y-4">
            <div className="border border-border bg-secondary px-4 py-3 text-sm"><span className="text-muted-foreground">Candidate: </span>{applicant.name} · {applicant.email}</div>
            <input value={password} onChange={(e) => setPassword(e.target.value)} type="text" placeholder="Set a temporary password" className={FIELD} data-testid="invite-password" />
            <p className="text-xs text-muted-foreground">Share this password with the candidate so they can log into the Candidate Portal to complete their HR forms.</p>
            <button type="submit" disabled={saving} data-testid="invite-submit" className="inline-flex w-full items-center justify-center gap-2 bg-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Create account</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Candidates + HR forms ---------------- */
function Candidates() {
  const [cands, setCands] = useState(null);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const load = () => adminCandidates().then(setCands).catch(() => setCands([]));
  useEffect(() => { load(); }, []);
  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.password.length < 6) return toast.error("Name, email and a 6+ char password are required.");
    setSaving(true);
    try { await createCandidate(form); toast.success("Candidate account created."); setForm({ name: "", email: "", password: "" }); setCreating(false); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-light tracking-tighter">Candidates</h1>
        <button onClick={() => setCreating((v) => !v)} data-testid="admin-new-candidate" className="inline-flex items-center gap-2 bg-brand px-5 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy"><Plus size={15} /> New account</button>
      </div>

      {creating && (
        <form onSubmit={add} data-testid="new-candidate-form" className="mt-6 grid gap-4 border border-border p-6 sm:grid-cols-4">
          <input name="name" value={form.name} onChange={change} placeholder="Full name" className={FIELD} data-testid="new-candidate-name" />
          <input name="email" type="email" value={form.email} onChange={change} placeholder="Email" className={FIELD} data-testid="new-candidate-email" />
          <input name="password" type="text" value={form.password} onChange={change} placeholder="Temp password" className={FIELD} data-testid="new-candidate-password" />
          <button type="submit" disabled={saving} data-testid="new-candidate-submit" className="inline-flex items-center justify-center gap-2 bg-brand px-6 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white hover:bg-navy disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create</button>
        </form>
      )}

      {cands === null ? (
        <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
      ) : cands.length === 0 ? (
        <p className="mt-10 text-muted-foreground">No candidate accounts yet. Create one here or from the Applicants tab.</p>
      ) : (
        <div className="mt-8 divide-y divide-border border-t border-border">
          {cands.map((c) => (
            <div key={c.id} data-testid={`admin-candidate-${c.id}`}>
              <button onClick={() => setExpanded(expanded === c.id ? null : c.id)} data-testid={`admin-candidate-toggle-${c.id}`} className="flex w-full items-center justify-between gap-4 py-5 text-left">
                <div>
                  <h3 className="font-display text-xl tracking-tight">{c.name || "Candidate"}</h3>
                  <div className="mt-1 font-mono text-[11px] text-muted-foreground">{c.email}</div>
                </div>
                <ChevronDown size={18} className={`text-muted-foreground transition-transform ${expanded === c.id ? "rotate-180 text-brand" : ""}`} />
              </button>
              {expanded === c.id && <HrForms candidate={c} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HrForms({ candidate }) {
  const [forms, setForms] = useState(null);
  const [form, setForm] = useState({ title: "", description: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => adminHrForms(candidate.id).then(setForms).catch(() => setForms([]));
  useEffect(() => { load(); }, [candidate.id]);
  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const assign = async (e) => {
    e.preventDefault();
    if (!form.title || !file) return toast.error("Add a title and a form file.");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("candidateId", candidate.id);
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("file", file);
      await createHrForm(fd);
      toast.success("Form assigned.");
      setForm({ title: "", description: "" }); setFile(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.title}"?`)) return;
    try { await deleteHrForm(f.id); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mb-6 border border-border bg-card/40 p-6">
      <form onSubmit={assign} data-testid={`assign-form-${candidate.id}`} className="grid gap-3 sm:grid-cols-12">
        <input name="title" value={form.title} onChange={change} placeholder="Form title (e.g. Form I-9)" className={`${FIELD} sm:col-span-4`} data-testid="hrform-title" />
        <input name="description" value={form.description} onChange={change} placeholder="Instructions (optional)" className={`${FIELD} sm:col-span-4`} data-testid="hrform-desc" />
        <label className="flex cursor-pointer items-center gap-2 border border-dashed border-border bg-secondary px-3 py-3 text-xs text-muted-foreground hover:border-brand sm:col-span-3" data-testid="hrform-file-label">
          <Upload size={14} /> {file ? file.name.slice(0, 18) : "Attach form"}
          <input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="hrform-file" />
        </label>
        <button type="submit" disabled={saving} data-testid="hrform-assign" className="inline-flex items-center justify-center gap-2 bg-brand px-4 py-3 font-mono text-[10px] uppercase tracking-[0.12em] text-white hover:bg-navy disabled:opacity-60 sm:col-span-1">{saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}</button>
      </form>

      {forms === null ? (
        <div className="mt-6 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
      ) : forms.length === 0 ? (
        <p className="mt-5 text-sm text-muted-foreground">No forms assigned to this candidate yet.</p>
      ) : (
        <div className="mt-5 space-y-2">
          {forms.map((f) => (
            <div key={f.id} data-testid={`hrform-${f.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-border px-4 py-3">
              <div>
                <div className="flex items-center gap-2"><span className="font-display text-base tracking-tight">{f.title}</span><span className={`font-mono text-[9px] uppercase tracking-[0.15em] ${f.status === "submitted" ? "text-brand" : "text-yellow-500"}`}>{f.status}</span></div>
                {f.description && <div className="mt-0.5 text-xs text-muted-foreground">{f.description}</div>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => adminDownloadHrForm(f.id, "blank", f.blank?.original_filename)} data-testid={`hrform-dl-blank-${f.id}`} className="border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground hover:text-foreground">Blank</button>
                {f.completed && <button onClick={() => adminDownloadHrForm(f.id, "completed", f.completed?.original_filename)} data-testid={`hrform-dl-completed-${f.id}`} className="border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-brand transition-colors hover:bg-brand hover:text-white">Completed</button>}
                <button onClick={() => remove(f)} data-testid={`hrform-delete-${f.id}`} className="grid h-8 w-8 place-items-center border border-border text-muted-foreground hover:text-destructive"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Clients ---------------- */
function Clients() {
  const [clients, setClients] = useState(null);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [saving, setSaving] = useState(false);
  const load = () => adminClients().then(setClients).catch(() => setClients([]));
  useEffect(() => { load(); }, []);
  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.password.length < 6) return toast.error("Name, email and a 6+ char password are required.");
    setSaving(true);
    try { await createClient(form); toast.success("Client account created."); setForm({ name: "", email: "", password: "", company: "" }); setCreating(false); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-light tracking-tighter">Clients</h1>
        <button onClick={() => setCreating((v) => !v)} data-testid="admin-new-client" className="inline-flex items-center gap-2 bg-brand px-5 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy"><Plus size={15} /> New account</button>
      </div>

      {creating && (
        <form onSubmit={add} data-testid="new-client-form" className="mt-6 grid gap-4 border border-border p-6 sm:grid-cols-5">
          <input name="company" value={form.company} onChange={change} placeholder="Company" className={FIELD} data-testid="new-client-company" />
          <input name="name" value={form.name} onChange={change} placeholder="Contact name" className={FIELD} data-testid="new-client-name" />
          <input name="email" type="email" value={form.email} onChange={change} placeholder="Email" className={FIELD} data-testid="new-client-email" />
          <input name="password" type="text" value={form.password} onChange={change} placeholder="Temp password" className={FIELD} data-testid="new-client-password" />
          <button type="submit" disabled={saving} data-testid="new-client-submit" className="inline-flex items-center justify-center gap-2 bg-brand px-6 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white hover:bg-navy disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create</button>
        </form>
      )}

      {clients === null ? (
        <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
      ) : clients.length === 0 ? (
        <p className="mt-10 text-muted-foreground">No client accounts yet.</p>
      ) : (
        <div className="mt-8 divide-y divide-border border-t border-border">
          {clients.map((c) => (
            <div key={c.id} data-testid={`admin-client-${c.id}`}>
              <button onClick={() => setExpanded(expanded === c.id ? null : c.id)} data-testid={`admin-client-toggle-${c.id}`} className="flex w-full items-center justify-between gap-4 py-5 text-left">
                <div>
                  <h3 className="font-display text-xl tracking-tight">{c.company || c.name}</h3>
                  <div className="mt-1 font-mono text-[11px] text-muted-foreground">{c.name} · {c.email}</div>
                </div>
                <ChevronDown size={18} className={`text-muted-foreground transition-transform ${expanded === c.id ? "rotate-180 text-brand" : ""}`} />
              </button>
              {expanded === c.id && <ClientWorkspace client={c} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClientWorkspace({ client }) {
  const [reqs, setReqs] = useState(null);
  const [docs, setDocs] = useState(null);
  const [req, setReq] = useState({ title: "", location: "", openings: 1, status: "open", notes: "" });
  const [savingReq, setSavingReq] = useState(false);
  const [docTitle, setDocTitle] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [savingDoc, setSavingDoc] = useState(false);
  const loadReqs = () => adminRequisitions(client.id).then(setReqs).catch(() => setReqs([]));
  const loadDocs = () => adminClientDocs(client.id).then(setDocs).catch(() => setDocs([]));
  useEffect(() => { loadReqs(); loadDocs(); }, [client.id]);

  const addReq = async (e) => {
    e.preventDefault();
    if (!req.title) return toast.error("Requisition title is required.");
    setSavingReq(true);
    try { await createRequisition({ ...req, clientId: client.id, openings: Number(req.openings) || 1 }); toast.success("Requisition created."); setReq({ title: "", location: "", openings: 1, status: "open", notes: "" }); loadReqs(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingReq(false); }
  };
  const setReqStatus = async (r, status) => {
    setReqs((prev) => prev.map((x) => (x.id === r.id ? { ...x, status } : x)));
    try { await updateRequisition(r.id, { title: r.title, location: r.location, openings: r.openings, status, notes: r.notes }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); loadReqs(); }
  };
  const removeReq = async (r) => { if (!window.confirm(`Delete requisition "${r.title}"?`)) return; try { await deleteRequisition(r.id); loadReqs(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const uploadDoc = async (e) => {
    e.preventDefault();
    if (!docTitle || !docFile) return toast.error("Add a title and a file.");
    setSavingDoc(true);
    try { const fd = new FormData(); fd.append("clientId", client.id); fd.append("title", docTitle); fd.append("file", docFile); await uploadClientDoc(fd); toast.success("Document shared."); setDocTitle(""); setDocFile(null); loadDocs(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingDoc(false); }
  };
  const removeDoc = async (d) => { if (!window.confirm(`Delete "${d.title}"?`)) return; try { await deleteClientDoc(d.id); loadDocs(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <div className="mb-6 space-y-6 border border-border bg-card/40 p-6">
      {/* Requisitions */}
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand">Requisitions</div>
        <form onSubmit={addReq} data-testid={`req-form-${client.id}`} className="mt-4 grid gap-3 sm:grid-cols-12">
          <input value={req.title} onChange={(e) => setReq({ ...req, title: e.target.value })} placeholder="Requisition title" className={`${FIELD} sm:col-span-4`} data-testid="req-title" />
          <input value={req.location} onChange={(e) => setReq({ ...req, location: e.target.value })} placeholder="Location" className={`${FIELD} sm:col-span-3`} data-testid="req-location" />
          <input type="number" min="1" value={req.openings} onChange={(e) => setReq({ ...req, openings: e.target.value })} placeholder="Openings" className={`${FIELD} sm:col-span-2`} data-testid="req-openings" />
          <select value={req.status} onChange={(e) => setReq({ ...req, status: e.target.value })} className={`${FIELD} sm:col-span-2`} data-testid="req-status">{REQ_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          <button type="submit" disabled={savingReq} data-testid="req-add" className="inline-flex items-center justify-center gap-2 bg-brand px-4 py-3 font-mono text-[10px] uppercase tracking-[0.12em] text-white hover:bg-navy disabled:opacity-60 sm:col-span-1">{savingReq ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}</button>
          <input value={req.notes} onChange={(e) => setReq({ ...req, notes: e.target.value })} placeholder="Notes (optional)" className={`${FIELD} sm:col-span-12`} data-testid="req-notes" />
        </form>

        {reqs === null ? (
          <div className="mt-5 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
        ) : reqs.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No requisitions yet.</p>
        ) : (
          <div className="mt-5 space-y-3">
            {reqs.map((r) => (
              <div key={r.id} data-testid={`req-${r.id}`} className="border border-border p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div><span className="font-display text-lg tracking-tight">{r.title}</span><span className="ml-3 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{r.location} · {r.openings} open</span></div>
                  <div className="flex items-center gap-2">
                    <select value={r.status} onChange={(e) => setReqStatus(r, e.target.value)} data-testid={`req-status-${r.id}`} className="border border-border bg-secondary px-2 py-1 font-mono text-[10px] uppercase tracking-[0.12em] outline-none focus:border-brand">{REQ_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
                    <button onClick={() => removeReq(r)} data-testid={`req-delete-${r.id}`} className="grid h-8 w-8 place-items-center border border-border text-muted-foreground hover:text-destructive"><Trash2 size={13} /></button>
                  </div>
                </div>
                <Submittals req={r} onChanged={loadReqs} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documents */}
      <div className="border-t border-border pt-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand">Documents & Reports</div>
        <form onSubmit={uploadDoc} data-testid={`doc-form-${client.id}`} className="mt-4 grid gap-3 sm:grid-cols-12">
          <input value={docTitle} onChange={(e) => setDocTitle(e.target.value)} placeholder="Document title (e.g. Q3 Report)" className={`${FIELD} sm:col-span-6`} data-testid="doc-title" />
          <label className="flex cursor-pointer items-center gap-2 border border-dashed border-border bg-secondary px-3 py-3 text-xs text-muted-foreground hover:border-brand sm:col-span-4" data-testid="doc-file-label"><Upload size={14} /> {docFile ? docFile.name.slice(0, 20) : "Attach file"}<input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setDocFile(e.target.files?.[0] || null)} className="hidden" data-testid="doc-file" /></label>
          <button type="submit" disabled={savingDoc} data-testid="doc-upload" className="inline-flex items-center justify-center gap-2 bg-brand px-4 py-3 font-mono text-[10px] uppercase tracking-[0.12em] text-white hover:bg-navy disabled:opacity-60 sm:col-span-2">{savingDoc ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Share</button>
        </form>
        {docs === null ? null : docs.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No documents shared yet.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {docs.map((d) => (
              <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center justify-between gap-3 border border-border px-4 py-3">
                <div className="flex items-center gap-2"><FileText size={16} className="text-brand" /><span className="text-sm">{d.title}</span></div>
                <div className="flex items-center gap-2">
                  <button onClick={() => adminDownloadClientDoc(d.id, d.file?.original_filename)} data-testid={`doc-dl-${d.id}`} className="border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground hover:text-foreground">Download</button>
                  <button onClick={() => removeDoc(d)} data-testid={`doc-delete-${d.id}`} className="grid h-8 w-8 place-items-center border border-border text-muted-foreground hover:text-destructive"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Submittals({ req, onChanged }) {
  const [sub, setSub] = useState({ candidateName: "", role: "", status: "submitted", note: "" });
  const [saving, setSaving] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (!sub.candidateName) return toast.error("Candidate name required.");
    setSaving(true);
    try { await addSubmittal(req.id, sub); setSub({ candidateName: "", role: "", status: "submitted", note: "" }); onChanged(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const setStatus = async (s, status) => { try { await updateSubmittal(req.id, s.id, { candidateName: s.candidateName, role: s.role, status, note: s.note }); onChanged(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const remove = async (s) => { try { await deleteSubmittal(req.id, s.id); onChanged(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <div className="mt-4 border-t border-border pt-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Submittals ({req.submittals?.length || 0})</div>
      {req.submittals?.length ? (
        <div className="mt-2 divide-y divide-border">
          {req.submittals.map((s) => (
            <div key={s.id} data-testid={`sub-${s.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
              <div><span>{s.candidateName}</span>{s.role && <span className="text-muted-foreground"> · {s.role}</span>}</div>
              <div className="flex items-center gap-2">
                <select value={s.status} onChange={(e) => setStatus(s, e.target.value)} data-testid={`sub-status-${s.id}`} className="border border-border bg-secondary px-2 py-1 font-mono text-[10px] uppercase tracking-[0.12em] outline-none focus:border-brand">{SUB_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}</select>
                <button onClick={() => remove(s)} data-testid={`sub-delete-${s.id}`} className="grid h-7 w-7 place-items-center border border-border text-muted-foreground hover:text-destructive"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <form onSubmit={add} data-testid={`sub-form-${req.id}`} className="mt-3 grid gap-2 sm:grid-cols-12">
        <input value={sub.candidateName} onChange={(e) => setSub({ ...sub, candidateName: e.target.value })} placeholder="Candidate name" className={`${FIELD} py-2 sm:col-span-4`} data-testid="sub-name" />
        <input value={sub.role} onChange={(e) => setSub({ ...sub, role: e.target.value })} placeholder="Role" className={`${FIELD} py-2 sm:col-span-3`} data-testid="sub-role" />
        <select value={sub.status} onChange={(e) => setSub({ ...sub, status: e.target.value })} className={`${FIELD} py-2 sm:col-span-3`} data-testid="sub-status-new">{SUB_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}</select>
        <button type="submit" disabled={saving} data-testid="sub-add" className="inline-flex items-center justify-center gap-1 border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] hover:border-brand hover:text-brand disabled:opacity-60 sm:col-span-2">{saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add</button>
      </form>
    </div>
  );
}

/* ---------------- Team ---------------- */
function Team() {
  const [admins, setAdmins] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const load = () => listAdmins().then(setAdmins).catch(() => setAdmins([]));
  useEffect(() => { load(); }, []);
  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.password.length < 6) return toast.error("Name, email, and a 6+ char password are required.");
    setSaving(true);
    try { await createAdmin(form); toast.success("Admin added."); setForm({ name: "", email: "", password: "" }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <h1 className="font-display text-3xl font-light tracking-tighter">Team</h1>
        {admins === null ? (
          <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
        ) : (
          <div className="mt-8 divide-y divide-border border-t border-border">
            {admins.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-4">
                <div>
                  <div className="font-display text-lg tracking-tight">{a.name || "Administrator"}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{a.email}</div>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-brand">admin</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="lg:col-span-5">
        <div className="border border-border p-6 sm:p-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand">Add administrator</div>
          <form onSubmit={add} data-testid="admin-add-form" className="mt-6 space-y-4">
            <input name="name" value={form.name} onChange={change} placeholder="Full name" className={FIELD} data-testid="new-admin-name" />
            <input name="email" type="email" value={form.email} onChange={change} placeholder="Email" className={FIELD} data-testid="new-admin-email" />
            <input name="password" type="password" value={form.password} onChange={change} placeholder="Temporary password" className={FIELD} data-testid="new-admin-password" />
            <button type="submit" disabled={saving} data-testid="new-admin-submit" className="inline-flex w-full items-center justify-center gap-2 bg-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy disabled:opacity-60">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add admin
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
